import { ApiResult, caller, urlMaker } from '@shared/utils';
import { IStatus, IStatusFormValues, IStatusLog, IStatusLogPersistence, IStatusMap, IStatusMapPersistence, IStatusPersistence } from '../interfaces';
import { CreateStatusErrorsMapper, CreateStatusMapper, StatusLogMapper, StatusMapMapper, StatusMapper } from '../mappers';

const appendToFormData = (body: FormData, payload: Record<string, string | undefined>) => {
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined) body.append(key, value);
  });
};

export class StatusesService {
  public static async getList(query: Record<string, any> = {}): Promise<ApiResult<200, { data: IStatus[]; total: number }> | ApiResult<400 | 500, string>> {
    const url = urlMaker('/api/admin/states/list', query);
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data: IStatus[] = (result.data || []).map((item: IStatusPersistence) => StatusMapper.toDomain(item));
        return new ApiResult(200, { data, total: data.length }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi', null);
    }
  }

  public static async getById(id: string | number): Promise<ApiResult<200, IStatus> | ApiResult<400 | 500, string>> {
    const url = urlMaker('/api/admin/states/getinfobystateid', { state_id: id });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, StatusMapper.toDomain(result.data), null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  }

  /** Creates when `id` is absent, edits when present — matches the old single-repo behaviour. */
  public static async save(values: IStatusFormValues): Promise<ApiResult<200, null> | ApiResult<422, Record<string, string>> | ApiResult<400 | 500, string>> {
    const url = values.id ? urlMaker('/api/admin/states/edit') : urlMaker('/api/admin/states/create');
    const body = new FormData();
    appendToFormData(body, CreateStatusMapper.toPersistence(values));

    try {
      const response = await caller(url, { method: 'POST', body });

      if (response.ok) {
        return new ApiResult(200, null, null);
      }

      if (response.status === 400) {
        const result = await response.json();
        return new ApiResult(422, CreateStatusErrorsMapper.toDomain(result.errors), null);
      }

      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  }

  public static async getByModelId(modelId: string | number): Promise<ApiResult<200, IStatus[]> | ApiResult<400 | 500, string>> {
    const url = urlMaker('/api/admin/states/getlistbymodelid', { model_id: modelId });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data: IStatus[] = (result.data || []).map((item: IStatusPersistence) => StatusMapper.toDomain(item));
        return new ApiResult(200, data, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  }

  public static async getHistory(params: { modelId: string | number; objectId: string | number }): Promise<ApiResult<200, IStatusLog[]> | ApiResult<400 | 500, string>> {
    const url = urlMaker('/api/admin/states/history', { model_id: params.modelId, objectId: params.objectId });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data: IStatusLog[] = (result.data || []).map((item: IStatusLogPersistence) => StatusLogMapper.toDomain(item));
        return new ApiResult(200, data, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  }

  public static async getStatusMap(params: { modelId: string | number; objectId: string | number }): Promise<ApiResult<200, IStatusMap[]> | ApiResult<400 | 500, string>> {
    const url = urlMaker('/api/client/get_status_map', { model_id: params.modelId, object_id: params.objectId });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data: IStatusMap[] = (result.data || result || []).map((item: IStatusMapPersistence) => StatusMapMapper.toDomain(item));
        return new ApiResult(200, data, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  }
}
